import React, { forwardRef, useMemo, useState, useCallback, useRef, useEffect } from "react";
import _ from "lodash";
import { Form, Button } from "antd";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import {
  FieldTypes,
  nameRegexValidator,
  urlRegexValidator,
} from "utils/formUtils";
import message from "components/CustomMessage";
import ModalUtil from "utils/modalUtil";
import BaseModalForm from "components/BaseModalForm";
import { duplicateNameValidator } from "utils/validatorUtil";
import { BitBucketOauthPopUp } from "./BitBucketOauthPopUp";
import AutoMappingSettingsForm, { GIT_TOOL_MAPPING_CONFIG } from "../common/AutoMappingSettingsForm";
import { useFeatureFlag } from "contexts/FeatureFlagContext";

const BitBucketConfigForm = forwardRef(
  ({ afterClose, configData, onUpdateConfigComponent, ...props }, ref) => {
    const featureFlags = useFeatureFlag();
    const toolConfigs = useRef();
    const [data, setData] = useState(configData || {});
    const [form] = Form.useForm();
    const { configList } = props.id;
    const toolName = _.toUpper(props?.id);
    const [setup, setSetup] = useState("Basic_Auth_Cloud");
    const [hostingType, setHostingType] = useState("Cloud");

    const handleMenuClick = useCallback(() => {
      ModalUtil.show({
        content: <BitBucketOauthPopUp />,
        afterClose: onUpdateConfigComponent
      });
      ModalUtil.closeAll();
    }, [onUpdateConfigComponent]);

    const initialConfig = useMemo(() => {
      const config = {};
      _.forEach(configData?.installationConfig?.subProductMappingConfig, entry => config[entry] = true);
      config.codeClassificationEnabled = !!props.codeClassificationinitialConfig?.codeClassificationEnabled || !!configData?.installationConfig?.codeClassificationEnabled || false;
      config.webhookEnabled = configData?.installationConfig?.webhookEnabled;
      toolConfigs.current = config;
      return config;
    }, [configData, props.codeClassificationinitialConfig]);

    const saveSettings = useCallback(async (id) => {
      const configs = toolConfigs.current;
      const payload = {
          subProductMappingConfig: featureFlags.SIMPLIFIED_ONBOARDING ?
            _.filter([
              GIT_TOOL_MAPPING_CONFIG.MAP_INACTIVE_REPOS,
              GIT_TOOL_MAPPING_CONFIG.MAP_DORMANT_REPOS
            ], (entry) => configs?.[entry]) : [],
          codeClassificationEnabled: configs?.codeClassificationEnabled,
          webhookEnabled: configs?.webhookEnabled
        }
    
      await ToolsIntegrationsAPI.getGithubInstallationConfigs(id, payload).promise;
    }, [featureFlags.SIMPLIFIED_ONBOARDING]);

    const onSettingsChange = useCallback((changedSetting, settings) => {
      toolConfigs.current = settings;
    }, []);

    const submitData = async (data) => {
      await form.validateFields();

      if (setup !== "OAuth_OnPrem") {
        const response = await ToolsIntegrationsAPI.createGitInstallation({
          ...data,
          repoType: toolName,
          token:
            data.password ||
            data.onPremPassword ||
            data.onPremToken ||
            data.token ||
            data.secretId ||
            data.secretKey,
        }).promise;
        await saveSettings(response.id);
        message.success(`Successfully configured.`);
      }
    };

    const baseModalProps = {
      okText: setup === "OAuth_OnPrem" ? "Close" : "Configure",
    };

    const {
      username,
      password,
      secretId,
      secretKey,
      onPremPassword,
      onPremToken,
      hostUrl,
    } = data;

    const [isHostUrlValid, setIsHostUrlValid] = useState(false);

    useEffect(() => {
      if (_.isEmpty(hostUrl)) {
        setIsHostUrlValid(false);
        return;
      }
      urlRegexValidator().validator(null, hostUrl)
        .then(() => setIsHostUrlValid(true))
        .catch(() => setIsHostUrlValid(false));
    }, [hostUrl]);

    const bitbucketWorkSpaceParams = useMemo(
      () => ({
        username,
        secretId,
        secretKey,
        ...(setup === "OAuth_Cloud" && { authenticationType: "OAUTH_CLOUD" }),
        token:
          setup === "Basic_Auth_Cloud"
            ? password
            : setup === "Basic_Auth_OnPrem"
            ? onPremPassword
            : onPremToken,
        repositoryType: toolName,
        hostUrl: hostUrl,
      }),
      [
        username,
        password,
        onPremPassword,
        onPremToken,
        toolName,
        setup,
        hostUrl,
        secretId,
        secretKey,
      ]
    );

    const getBitBucketSetupSpecificFields = useCallback(
      (setup) => {
        switch (setup) {
          case "Basic_Auth_Cloud":
            return [
              {
                name: "username",
                label: "Username",
                colSpan: 24,
                required: true,
              },
              {
                name: "password",
                label: "Password",
                type: FieldTypes.PASSWORD,
                help: "Enter a valid App password or Generate it",
                colSpan: 24,
                required: true,
              },
            ];
          case "OAuth_Cloud":
            return [
              {
                name: "secretId",
                label: "Key",
                colSpan: 24,
                required: true,
              },
              {
                name: "secretKey",
                label: "Secret",
                type: FieldTypes.PASSWORD,
                help: "Enter a valid Secret or Generate it",
                colSpan: 24,
                required: true,
              },
            ];
          case "Basic_Auth_OnPrem":
            return [
              {
                name: "username",
                label: "Username",
                colSpan: 24,
                required: true,
              },
              {
                name: "onPremPassword",
                label: "Password",
                type: FieldTypes.PASSWORD,
                help: "Enter a valid password or Generate it",
                colSpan: 24,
                required: true,
              },
              {
                name: "hostUrl",
                label: "Host Url",
                rules: [urlRegexValidator()],
                colSpan: 24,
                required: true,
              },
            ];
          case "Session_Token_Auth_OnPrem":
            return [
              {
                name: "onPremToken",
                label: "Token",
                type: FieldTypes.PASSWORD,
                help: "Enter a valid token or Generate it",
                colSpan: 24,
                required: true,
              },
              {
                name: "hostUrl",
                label: "Host Url",
                rules: [urlRegexValidator()],
                colSpan: 24,
                required: true,
              },
            ];
          case "OAuth_OnPrem":
            return [
              {
                colSpan: 24,
                render: () => (
                  <Button
                    className="m-r-m"
                    onClick={handleMenuClick}
                    type="primary"
                    block
                  >
                    Bitbucket On-Prem OAuth Integration
                  </Button>
                ),
              },
            ];
          default:
            return [];
        }
      },
      [handleMenuClick]
    );

    const bitbucketFields = useMemo(
      () => [
        {
          name: "hostingType",
          label: "Hosting Type",
          type: FieldTypes.SELECT,
          colSpan: 24,
          required: true,
          options: [
            { label: "Cloud", value: "Cloud" },
            { label: "On-Prem", value: "OnPrem" },
          ],
          props: {
            onChange: (value) => {
              setHostingType(value);
              const defaultSetup =
                value === "Cloud" ? "Basic_Auth_Cloud" : "Basic_Auth_OnPrem";
              setSetup(defaultSetup);
              form.setFieldsValue(
                value === "Cloud"
                  ? { setupEnvforCloud: defaultSetup }
                  : { setupEnvforOnPrem: defaultSetup }
              );
            },
            defaultValue: "Cloud",
          },
        },
        ...(hostingType === "Cloud"
          ? [
              {
                name: "setupEnvforCloud",
                type: FieldTypes.RADIO,
                options: [
                  { label: "Basic Auth", value: "Basic_Auth_Cloud" },
                  { label: "OAuth", value: "OAuth_Cloud" },
                ],
                props: {
                  onChange: (e) => setSetup(e.target.value),
                  defaultValue: "Basic_Auth_Cloud",
                },
              },
            ]
          : []),
        ...(hostingType === "OnPrem"
          ? [
              {
                name: "setupEnvforOnPrem",
                type: FieldTypes.RADIO,
                options: [
                  { label: "Basic Auth", value: "Basic_Auth_OnPrem" },
                  {
                    label: "Personal Access Token",
                    value: "Session_Token_Auth_OnPrem",
                  },
                  { label: "OAuth", value: "OAuth_OnPrem" },
                ],
                props: {
                  onChange: (e) => setSetup(e.target.value),
                  defaultValue: "Basic_Auth_OnPrem",
                },
              },
            ]
          : []),

        ...getBitBucketSetupSpecificFields(setup),

        ...(setup === "OAuth_OnPrem"
          ? []
          : [
              {
                name: "workspace",
                label: "Workspace",
                type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
                colSpan: 24,
                required: true,
                props: ((username && password) ||
                  (secretId && secretKey) ||
                  (username && onPremPassword && isHostUrlValid) ||
                  (onPremToken && isHostUrlValid)) && {
                  api: ToolsIntegrationsAPI.postWorkSpaces,
                  apiParams: bitbucketWorkSpaceParams,
                  parseSelectOptions: bitbucketWorkspaceOptions,
                },
              },
            ]),
      ],
      [
        username,
        password,
        secretId,
        secretKey,
        onPremPassword,
        onPremToken,
        bitbucketWorkSpaceParams,
        setup,
        isHostUrlValid,
        hostingType,
        getBitBucketSetupSpecificFields,
        form
      ]
    );

    const fields = useMemo(
      () => [
        ...(setup !== "OAuth_OnPrem"
          ? [
              {
                name: "name",
                label: "Name",
                props: {
                  autoFocus: true,
                },
                colSpan: 24,
                required: true,
                rules: [
                  nameRegexValidator,
                  duplicateNameValidator({ list: configList }),
                ],
              },
            ]
          : []),

        ...(bitbucketFields || {}),
      ],
      [configList, bitbucketFields, setup]
    );

    const onValuesChange = useCallback((_, values) => {
      setData((data) => ({ ...data, ...values }));
    }, []);

    return (
      <BaseModalForm
        modalProps={baseModalProps}
        ref={ref}
        form={form}
        fields={fields}
        onOk={submitData}
        onValuesChange={onValuesChange}
        afterClose={afterClose}
        title={`Configure ${_.replace(props?.id, "_", " ")}`}
        initialValues={{
          hostingType  : "Cloud",
          hostUrl: "",
          name: configData?.name,
          workspace: configData?.workspace,
        }}
      >
        {setup !== "OAuth_OnPrem" && <AutoMappingSettingsForm onChange={onSettingsChange} values={props.codeClassificationinitialConfig ?? initialConfig} />}
      </BaseModalForm>
    );
  }
);

export default BitBucketConfigForm;

function bitbucketWorkspaceOptions(options) {
  return options.map((option) => ({
    label: _.last(option.split("/")),
    value: option,
  }));
}
